/* eslint-disable no-return-assign */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Col, Nav, NavItem, NavLink } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import '../assets/css/componentStyles/SettingsPage.css';

import InformationsForm from './informations/forms/InformationsForm';
import validationSchema from './informations/forms/InformationsForm.schema';
import { fetchOrganizationDetails } from './SettingsActions';
import MyProfilePage from './myprofile/MyProfilePage';
import SearchAlertsPage from './searchalerts/SearchAlertsPage';
// import MarketingPreferencesPage from './notifications/MarketingPreferencesPage';
import TransactionsPage from './transactions/TransactionsPage';
import InvoicesPage from './invoices/InvoicesPage';
import AccountPasswordForm from './forms/AccountPasswordForm';
import PaymentCardPage from './paymentcard/PaymentCardPage';
import SettingsSectionHeader from './SettingsSectionHeader';
import MarketingPreferencesPage from './marketingpreferences/MarketingPreferencesPage';
import NotificationsPage from './notifications/NotificationsPage';

const SETTINGS_SECTIONS = {
  COMPANY_DETAILS: 'company-details',
  PAYMENT_CARD: 'payment-card',
  TRANSACTIONS: 'transactions',
  INVOICES: 'invoices',
  MY_PROFILE: 'my-profile',
  ACCOUNT_PASSWORD: 'account-password',
  SEARCH_ALERTS: 'search-alerts',
  NOTIFICATIONS: 'notifications',
  MARKETING_PREFERENCES: 'marketing-preferences',
};

const SECTION_LABELS = {
  [SETTINGS_SECTIONS.COMPANY_DETAILS]: 'COMPANY DETAILS',
  [SETTINGS_SECTIONS.PAYMENT_CARD]: 'PAYMENT CARD',
  [SETTINGS_SECTIONS.TRANSACTIONS]: 'TRANSACTIONS',
  [SETTINGS_SECTIONS.INVOICES]: 'INVOICES',
  [SETTINGS_SECTIONS.MY_PROFILE]: 'MY PROFILE',
  [SETTINGS_SECTIONS.ACCOUNT_PASSWORD]: 'ACCOUNT PASSWORD',
  [SETTINGS_SECTIONS.SEARCH_ALERTS]: 'SEARCH ALERTS',
  [SETTINGS_SECTIONS.NOTIFICATIONS]: 'NOTIFICATIONS',
  [SETTINGS_SECTIONS.MARKETING_PREFERENCES]: 'MARKETING PREFERENCES',
};

const PAYMENT_CARD_INITIAL_VALUES = {
  cardholder: '',
};

function SettingsPage() {
  const dispatch = useDispatch();
  const sectionRefs = useRef({});

  const organizationDetails = useSelector((state) =>
    state.myOrganization.get('organizationDetails'),
  );

  useEffect(() => {
    dispatch(fetchOrganizationDetails());
  }, [dispatch]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handlePaymentCardSubmit = useCallback(() => null, []);

  const prepareAdditionalLocationObject = useCallback(() => {
    if (organizationDetails && organizationDetails?.additionalLocation) {
      return organizationDetails.additionalLocation.map((item) => ({
        id: item?.id,
        location: item?.name || '',
        address: item?.address,
        address2: item?.address2,
        city: item?.city,
        county: item?.county,
        postalCode: item?.postalCode
          ? {
              value: item.postalCode.id,
              label: item.postalCode.postcode,
            }
          : '',
        status: item?.status,
        version: item?.version,
        canBeDeleted: item?.canBeDeleted,
      }));
    }
    return null;
  }, [organizationDetails]);

  const companyDetailsInitialValues = useMemo(
    () => ({
      tradeName: organizationDetails?.tradeName,
      companyName: organizationDetails?.companyName,
      companyRegistrationNumber: organizationDetails?.companyRegistrationNumber,
      vatNumber: organizationDetails?.vatNumber,
      mobileNumber: organizationDetails?.mobileNumber,
      landlinePhoneNumber: organizationDetails?.landlinePhoneNumber,
      landlinePhoneNumberIsPending: organizationDetails?.landlinePhoneNumberIsPending,
      website: organizationDetails?.website,
      location: organizationDetails?.mainLocation?.name || '',
      postalCodeId: organizationDetails?.mainLocation?.postalCode
        ? {
            value: organizationDetails.mainLocation.postalCode.id,
            label: organizationDetails.mainLocation.postalCode.postcode,
          }
        : '',
      city: organizationDetails?.mainLocation?.city,
      county: organizationDetails?.mainLocation?.county,
      address: organizationDetails?.mainLocation?.address,
      address2: organizationDetails?.mainLocation?.address2,
      mainLocationIsPending: organizationDetails?.mainLocationIsPending,
      additionalLocation: prepareAdditionalLocationObject() || [
        { postalCode: '', address: '', address2: '', city: '', county: '' },
      ],
      mainLocationVersion: organizationDetails?.mainLocation?.version,
    }),
    [organizationDetails, prepareAdditionalLocationObject],
  );

  const navigationItems = useMemo(
    () =>
      Object.entries(SECTION_LABELS).map(([sectionKey, sectionLabel]) => (
        <NavItem key={sectionKey}>
          <NavLink className="settings-nav-link" onClick={() => scrollToSection(sectionKey)}>
            {sectionLabel}
          </NavLink>
        </NavItem>
      )),
    [scrollToSection],
  );

  const createRefCallback = useCallback(
    (sectionKey) => (el) => {
      sectionRefs.current[sectionKey] = el;
    },
    [],
  );

  const companyDetailsRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.COMPANY_DETAILS),
    [createRefCallback],
  );
  const paymentCardRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.PAYMENT_CARD),
    [createRefCallback],
  );
  const transactionsRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.TRANSACTIONS),
    [createRefCallback],
  );
  const invoicesRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.INVOICES),
    [createRefCallback],
  );
  const myProfileRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.MY_PROFILE),
    [createRefCallback],
  );
  const accountPasswordRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.ACCOUNT_PASSWORD),
    [createRefCallback],
  );
  const searchAlertsRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.SEARCH_ALERTS),
    [createRefCallback],
  );
  const notificationsRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.NOTIFICATIONS),
    [createRefCallback],
  );
  const marketingPreferencesRef = useMemo(
    () => createRefCallback(SETTINGS_SECTIONS.MARKETING_PREFERENCES),
    [createRefCallback],
  );

  return (
    <div className="settings-page">
      <div className="settings-page-header">
        <div className="settings-page-header-title">
          <h3>Settings</h3>
        </div>
      </div>
      <div className="settings-container">
        <div className="settings-page-content">
          <Col md="3" className="settings-nav-sidebar d-none d-xl-block">
            <div className="settings-nav-container">
              <Nav vertical className="settings-nav">
                {navigationItems}
              </Nav>
            </div>
          </Col>

          <Col xl="9" className="settings-content">
            <div
              id={SETTINGS_SECTIONS.COMPANY_DETAILS}
              className="settings-section"
              ref={companyDetailsRef}
            >
              <SettingsSectionHeader
                header="Company Details"
                description="Please complete the information about your company details, contact and its locations."
              />
              <Formik
                initialValues={companyDetailsInitialValues}
                validateOnBlur={true}
                enableReinitialize
                validationSchema={validationSchema}
              >
                {({ values, setSubmitting, isValid, setFieldValue }) => (
                  <InformationsForm
                    setFieldValue={setFieldValue}
                    values={values}
                    setSubmitting={setSubmitting}
                    isValid={isValid}
                  />
                )}
              </Formik>
            </div>

            <div
              id={SETTINGS_SECTIONS.PAYMENT_CARD}
              className="settings-section"
              ref={paymentCardRef}
            >
              <SettingsSectionHeader
                header="Payment Card"
                description="Below you will find card payment details."
              />
              <Formik
                initialValues={PAYMENT_CARD_INITIAL_VALUES}
                onSubmit={handlePaymentCardSubmit}
                validateOnBlur={true}
                enableReinitialize
              >
                {({ values }) => <PaymentCardPage values={values} />}
              </Formik>
            </div>

            <div
              id={SETTINGS_SECTIONS.TRANSACTIONS}
              className="settings-section"
              ref={transactionsRef}
            >
              <SettingsSectionHeader
                header="Transactions"
                description="In the table you can find all transactions made on The Stock Vault platform."
              />
              <TransactionsPage />
            </div>

            <div
              id={SETTINGS_SECTIONS.INVOICES}
              className="settings-section"
              ref={invoicesRef}
            >
              <SettingsSectionHeader
                header="Invoices"
                description="In the table you can find a list of invoices for transactions on The Stock Vault platform."
              />
              <InvoicesPage />
            </div>

            <div
              id={SETTINGS_SECTIONS.MY_PROFILE}
              className="settings-section"
              ref={myProfileRef}
            >
              <SettingsSectionHeader
                header="My Profile"
                description={
                  <>
                    Here you can check the details of your profile are correct. If you wish to
                    make any changes to your data, please contact The Stock Vault support -{' '}
                    <b>support@thestockvault.com</b>
                  </>
                }
              />
              <MyProfilePage />
            </div>

            <div
              id={SETTINGS_SECTIONS.ACCOUNT_PASSWORD}
              className="settings-section"
              ref={accountPasswordRef}
            >
              <SettingsSectionHeader
                header="Account Password"
                description="Here you can change your login password."
              />
              <AccountPasswordForm />
            </div>

            <div
              id={SETTINGS_SECTIONS.SEARCH_ALERTS}
              className="settings-section"
              ref={searchAlertsRef}
            >
              <SettingsSectionHeader
                header="Search Alerts"
                description="Below you will find a list of created search alerts - you can edit them and new ones."
              />
              <SearchAlertsPage />
            </div>

            <div
              id={SETTINGS_SECTIONS.NOTIFICATIONS}
              className="settings-section"
              ref={notificationsRef}
            >
              <SettingsSectionHeader
                header="Notifications"
                description="Please choose your preferred method for receiving system notifications."
              />
              <NotificationsPage />
            </div>

            <div
              id={SETTINGS_SECTIONS.MARKETING_PREFERENCES}
              className="settings-section"
              ref={marketingPreferencesRef}
            >
              <SettingsSectionHeader
                header="Marketing preferences"
                description="Choose how you want to hear about the latest products, services and offers from us. By selecting the below boxes, you're consenting to receive updates from The Stock Vault, via the communication methods you choose. We may occasionally pass on your details to organisation we think you might be interested in, but only people we trust and work closely with."
              />
              <MarketingPreferencesPage />
            </div>
          </Col>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
